import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Send, Loader2, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QR_IMG = "https://customer-assets.emergentagent.com/job_qr-wash-earn/artifacts/7wxwocj9_1000004107.png";

export default function ContactSection() {
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.business_name || !form.contact_name || !form.phone || !form.email) {
      setError("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-[#0d0d0d]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 mb-4 block">
            Get Started
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
            Ready to Partner Up?
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Lead Capture Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            {submitted ? (
              <div
                data-testid="form-success-message"
                className="bg-neutral-900 border border-neutral-800 rounded-2xl p-12 text-center"
              >
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
                <p className="text-neutral-400">
                  We received your information. Someone from our team will reach out shortly.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="lead-capture-form"
                className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 space-y-5"
              >
                <div>
                  <Label className="text-neutral-300 mb-2 block text-sm">Business Name *</Label>
                  <Input
                    name="business_name"
                    data-testid="input-business-name"
                    value={form.business_name}
                    onChange={handleChange}
                    placeholder="Your Car Wash Name"
                    className="bg-neutral-950 border-neutral-800 text-white placeholder:text-neutral-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <Label className="text-neutral-300 mb-2 block text-sm">Contact Name *</Label>
                  <Input
                    name="contact_name"
                    data-testid="input-contact-name"
                    value={form.contact_name}
                    onChange={handleChange}
                    placeholder="Your Full Name"
                    className="bg-neutral-950 border-neutral-800 text-white placeholder:text-neutral-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-neutral-300 mb-2 block text-sm">Phone *</Label>
                    <Input
                      name="phone"
                      data-testid="input-phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="bg-neutral-950 border-neutral-800 text-white placeholder:text-neutral-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <Label className="text-neutral-300 mb-2 block text-sm">Email *</Label>
                    <Input
                      name="email"
                      type="email"
                      data-testid="input-email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="bg-neutral-950 border-neutral-800 text-white placeholder:text-neutral-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-neutral-300 mb-2 block text-sm">Notes (Optional)</Label>
                  <Textarea
                    name="notes"
                    data-testid="input-notes"
                    value={form.notes}
                    onChange={handleChange}
                    placeholder="Tell us about your car wash..."
                    rows={3}
                    className="bg-neutral-950 border-neutral-800 text-white placeholder:text-neutral-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                {error && (
                  <p data-testid="form-error-message" className="text-red-400 text-sm">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="submit-lead-button"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 py-4 transition-all duration-300 transform hover:-translate-y-1 shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] font-bold text-sm tracking-wide disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Submit <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>

          {/* QR Code + Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="flex flex-col items-center lg:items-start justify-center gap-10"
          >
            <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 w-full max-w-sm text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 mb-5">
                Scan to Get Started
              </p>
              <img
                src={QR_IMG}
                alt="AnyWash Pass QR Code"
                data-testid="qr-code-image"
                className="w-48 h-48 mx-auto mb-4 rounded-xl object-contain bg-white p-2"
              />
              <p className="text-neutral-500 text-sm">
                Scan this QR code to begin the onboarding process
              </p>
            </div>

            <div className="space-y-4 w-full max-w-sm">
              <a
                href="tel:817-442-2425"
                data-testid="contact-phone"
                className="flex items-center gap-4 bg-neutral-900 border border-neutral-800 rounded-2xl p-5 hover:border-neutral-700 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-600/10 border border-blue-500/20 flex items-center justify-center group-hover:bg-blue-600/20 transition-colors">
                  <Phone className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p className="text-neutral-500 text-xs font-semibold uppercase tracking-wider">Phone</p>
                  <p className="text-white font-bold">817-442-2425</p>
                </div>
              </a>
              <a
                href="mailto:ana@anywashpass.com"
                data-testid="contact-email"
                className="flex items-center gap-4 bg-neutral-900 border border-neutral-800 rounded-2xl p-5 hover:border-neutral-700 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-600/10 border border-blue-500/20 flex items-center justify-center group-hover:bg-blue-600/20 transition-colors">
                  <Mail className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p className="text-neutral-500 text-xs font-semibold uppercase tracking-wider">Email</p>
                  <p className="text-white font-bold">ana@anywashpass.com</p>
                </div>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
